import { useParams } from 'react-router-dom'
import { useEffect, useState, useRef } from 'react'
import { fetchProject, openProjectStream } from '../lib/api'
import type { VideoProject, SSEEvent } from '../lib/types'
import PlayerShell from '../components/PlayerShell'
import VersionDropdown from '../components/VersionDropdown'
import ProcessingBanner from '../components/ProcessingBanner'
import ErrorBanner from '../components/ErrorBanner'
import { Download, Share2, Copy, Check } from 'lucide-react'

/** Extract a short readable name from a video source URL, or empty if not derivable. */
function deriveVideoName(sourceUrl: string): string {
  try {
    const u = new URL(sourceUrl)
    const parts = u.pathname.split('/').filter(Boolean)
    const last = parts[parts.length - 1] || ''
    // Strip query-string artifacts if any leaked
    const clean = last.split('?')[0]
    // Decode and drop the file extension for a cleaner title
    const decoded = decodeURIComponent(clean)
    const noExt = decoded.replace(/\.[a-z0-9]{2,4}$/i, '')
    // Cap length so it doesn't wrap the header
    return noExt.length > 60 ? noExt.slice(0, 57) + '...' : noExt
  } catch {
    return ''
  }
}

export default function PlayerPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const [project, setProject] = useState<VideoProject | null>(null)
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [newVersionAvailable, setNewVersionAvailable] = useState<number | null>(null)
  const [copied, setCopied] = useState(false)
  const eventSourceRef = useRef<EventSource | null>(null)

  useEffect(() => {
    if (!projectId) return
    let cancelled = false

    setLoading(true)
    setError(null)

    fetchProject(projectId)
      .then((p) => {
        if (cancelled) return
        if (!p) {
          setError('This project does not exist or has been deleted.')
          setLoading(false)
          return
        }
        setProject(p)
        setSelectedVersion(p.latest_ready_version ?? p.versions.at(-1)?.version ?? null)
        setLoading(false)
      })
      .catch((e) => {
        if (cancelled) return
        setError(`Failed to load project: ${e.message}`)
        setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [projectId])

  // SSE stream: listen for new versions
  useEffect(() => {
    if (!projectId || !project) return

    const es = openProjectStream(projectId)
    eventSourceRef.current = es

    const onMessage = (ev: MessageEvent) => {
      try {
        const data: SSEEvent = JSON.parse(ev.data)
        if (data.event === 'version_started') {
          setProject((prev) => {
            if (!prev) return prev
            const exists = prev.versions.some((v) => v.version === data.version)
            if (exists) return prev
            return {
              ...prev,
              versions: [
                ...prev.versions,
                {
                  version: data.version,
                  status: 'processing',
                  spec_hash: data.spec_hash,
                  ops: [],
                  created_at: data.timestamp,
                },
              ],
            }
          })
        } else if (data.event === 'version_ready') {
          setProject((prev) => {
            if (!prev) return prev
            const versions = prev.versions.map((v) =>
              v.version === data.version
                ? { ...v, status: 'ready' as const, output_url: data.output_url, duration_seconds: data.duration_seconds, ready_at: data.timestamp }
                : v
            )
            const hasIt = versions.some((v) => v.version === data.version)
            if (!hasIt) {
              versions.push({
                version: data.version,
                status: 'ready',
                spec_hash: 'sha256:unknown',
                ops: [],
                output_url: data.output_url,
                duration_seconds: data.duration_seconds,
                created_at: data.timestamp,
                ready_at: data.timestamp,
              })
            }
            return { ...prev, versions, latest_ready_version: data.version }
          })
          setNewVersionAvailable(data.version)
        } else if (data.event === 'version_failed') {
          setError(`Version ${data.version} failed: ${data.error_message} (${data.error_code})`)
        }
      } catch (e) {
        console.warn('malformed SSE event', e)
      }
    }

    es.onmessage = onMessage
    es.onerror = () => {
      // Non-fatal; EventSource auto-reconnects
      console.warn('SSE connection error, will retry')
    }

    return () => {
      es.close()
      eventSourceRef.current = null
    }
  }, [projectId, project?.project_id])

  const currentVersion = project?.versions.find((v) => v.version === selectedVersion) ?? null

  const handleSwitchToLatest = () => {
    if (newVersionAvailable !== null) {
      setSelectedVersion(newVersionAvailable)
      setNewVersionAvailable(null)
    }
  }

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard blocked; ignore silently
    }
  }

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-16 text-center text-slate-500">
        Loading project...
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-16">
        <ErrorBanner message={error} onRetry={() => window.location.reload()} />
      </div>
    )
  }

  if (!project || !currentVersion) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-16 text-center text-slate-500">
        No version available for this project yet.
      </div>
    )
  }

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
      <div className="mb-6 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">
            {project.customer_name ? (
              <>Signed in as <span className="font-semibold text-slate-900">{project.customer_name}</span></>
            ) : 'Video project'}
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-1" title={project.source_url}>
            {deriveVideoName(project.source_url) || `Project ${project.project_id.slice(0, 8)}`}
          </h1>
          <div className="text-xs font-mono text-slate-400">{project.project_id}</div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleCopyLink}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:border-slate-300 transition"
          >
            {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
            {copied ? 'Copied' : 'Copy link'}
          </button>
          <a
            href={currentVersion.output_url}
            download
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:border-slate-300 transition"
          >
            <Download className="h-4 w-4" />
            Download v{currentVersion.version}
          </a>
          <button
            onClick={handleCopyLink}
            className="inline-flex items-center gap-2 rounded-lg bg-ub-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-ub-blue-700 transition"
          >
            <Share2 className="h-4 w-4" />
            Share
          </button>
        </div>
      </div>

      {newVersionAvailable !== null && newVersionAvailable !== selectedVersion && (
        <ProcessingBanner
          message={`Version ${newVersionAvailable} is ready.`}
          actionLabel="Switch now"
          onAction={handleSwitchToLatest}
        />
      )}

      <PlayerShell
        key={currentVersion.version}
        src={currentVersion.output_url ?? ''}
        poster={currentVersion.poster_url}
        title={`${project.project_id} - v${currentVersion.version}`}
        thumbnails={currentVersion.thumbnails_url}
        tracks={currentVersion.tracks}
      />

      <div className="mt-6 flex items-start justify-between gap-6 flex-wrap">
        <div>
          <VersionDropdown
            versions={project.versions}
            selectedVersion={selectedVersion}
            onSelect={setSelectedVersion}
          />
        </div>
        <div className="text-sm text-slate-600 max-w-md">
          <div className="font-semibold text-slate-900 mb-1">Applied ops (v{currentVersion.version})</div>
          <ul className="space-y-1 font-mono text-xs">
            {currentVersion.ops.map((op, i) => (
              <li key={i} className="rounded bg-slate-100 px-2 py-1">
                {JSON.stringify(op)}
              </li>
            ))}
            {currentVersion.ops.length === 0 && (
              <li className="text-slate-400 italic">no ops declared</li>
            )}
          </ul>
        </div>
      </div>
    </div>
  )
}
